import { tracker } from '@lessonup/analytics';
import {
  color,
  IconArrowLeft,
  IconLesson,
  NiceModalHandler,
  spacing,
  styled,
  TextElement,
  TextField,
  Truncate,
  UploadFileInput,
  useForm,
  useWatch,
} from '@lessonup/ui-components';
import { removeFileExtension } from '@lessonup/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACE } from '../constants';
import { Footer } from './Footer';
import { LessonModalContent, LessonModalMainWrapper } from './LayoutComponents';

interface ImportLessonProps {
  modal: NiceModalHandler<Record<string, unknown>>;
  loading: boolean;
  onCreateLessonFromSlideDeck: (name: string, file: File) => Promise<void>;
  invalidFileError: string | undefined;
  onSelectFile: (file: File) => void;
  back: () => void;
}

export const ImportLesson: React.FC<ImportLessonProps> = ({
  back,
  loading,
  onCreateLessonFromSlideDeck,
  invalidFileError,
  onSelectFile,
}) => {
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<{ lessonTitle: string }>();

  const { t } = useTranslation(TRANSLATION_NAMESPACE, { keyPrefix: 'importLesson' });

  const lessonTitle = useWatch({
    control,
    name: 'lessonTitle',
    defaultValue: '',
  });

  const [file, setFile] = useState<File | undefined>(undefined);

  const handleFileUpload = async (files: File[]) => {
    if (files.length > 1) {
      throw new Error('Only one file can be uploaded at a time');
    }
    onSelectFile(files[0]);
    setFile(files[0]);
    const filenameWithoutExtension = files[0]?.name ? removeFileExtension(files[0].name) : '';
    setValue('lessonTitle', filenameWithoutExtension, { shouldValidate: true });
  };

  const handleImportLesson = () => {
    if (!lessonTitle || !file || loading) return;
    tracker.events.lessonPowerpointStartConversion({ fileSizeInBytes: file.size.toString() });
    const trimmedLessonTitle = lessonTitle.trim();
    return onCreateLessonFromSlideDeck(trimmedLessonTitle, file);
  };

  return (
    <StyledForm onSubmit={handleSubmit(handleImportLesson)}>
      <LessonModalMainWrapper>
        <LessonModalContent>
          <StyledFileSelectorElement>
            <UploadFileInput
              onUpload={handleFileUpload}
              disabled={loading}
              buttonType="neutral"
              buttonStroke
              multiple={false}
              acceptedMimeTypes={{
                documentTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
                audioTypes: [],
                imageTypes: [],
                videoTypes: [],
              }}
              useCustomErrorHandling={true}
            >
              {t('slideDeck.importButton.label')}
            </UploadFileInput>
            {file && (
              <TextElement textStyle="bodyText" size="small">
                <Truncate as="span" maxLines={2}>
                  {file.name}
                </Truncate>
              </TextElement>
            )}
          </StyledFileSelectorElement>
          {invalidFileError && <ErrorText textStyle="bodyText">{invalidFileError}</ErrorText>}
          <TextField
            size="medium"
            type="text"
            label={t('lessonTitle')}
            disabled={!file}
            {...register('lessonTitle', {
              required: { value: true, message: t('errors.lessonTitleFieldIsRequired') },
              maxLength: { value: 300, message: t('errors.maxLengthExceeded') },
              setValueAs: (value) => (value && value.trim()) || undefined,
              onChange: (e) => setValue('lessonTitle', e.target.value),
            })}
            placeholder={file ? t('slideDeck.lessonTitlePlaceholder') : t('slideDeck.noFileAddedTitlePlaceholder')}
            {...(errors['lessonTitle']?.message
              ? { validation: { type: 'negative', message: errors['lessonTitle'].message } }
              : {})}
          />
        </LessonModalContent>
        <Footer
          backButton={{ onClick: back, label: t('back'), iconStart: <IconArrowLeft /> }}
          nextButton={{
            onClick: handleSubmit(handleImportLesson),
            label: t('importLesson'),
            disabled: !file,
            buttonType: 'primary',
            iconEnd: <IconLesson />,
          }}
        />
      </LessonModalMainWrapper>
    </StyledForm>
  );
};

const StyledFileSelectorElement = styled.div`
  max-width: 100%;
  overflow: hidden;
  gap: ${spacing.size8};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ErrorText = styled(TextElement)`
  color: ${color.additional.error.background};
  margin-top: ${spacing.size8};
`;
